import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  RefreshCw, Plus, Search, Edit3, Trash2, X, Save, Loader2, ClipboardList, FileSpreadsheet, Upload, Filter,
} from 'lucide-react';
import { api, API_BASE, getToken } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

// Full program names mapped to their abbreviations
export const AID_PROGRAMS = [
  { code: 'DAP',  name: 'Accounting & Payroll' },
  { code: 'DMM',  name: 'Digital Marketing Management' },
  { code: 'HRA',  name: 'Human Resource Administration' },
  { code: 'DIB',  name: 'International Business' },
  { code: 'MAD',  name: 'Marketing Administrator' },
  { code: 'OA',   name: 'Office Administrator' },
  { code: 'ACSW', name: 'Addiction Community Support Worker' },
  { code: 'HCA',  name: 'Health Care Assistant' },
  { code: 'MOA',  name: 'Medical Office Assistant' },
  { code: 'SSSW', name: 'Social Services Support' },
  { code: 'HMD',  name: 'Hospitality Management' },
  { code: 'NSA',  name: 'Network Support Administrator' },
  { code: 'NST',  name: 'Network Support Technician' },
];
const PROVINCES = ['BC', 'AB', 'SK', 'MB', 'ON', 'QC', 'NS', 'NB', 'PE', 'NL', 'YT', 'NT', 'NU'];
const ACTIONS = ['', 'Pending', 'ISSUE LOA', 'ISSUE CLOA', 'LOA ISSUED', 'CLOA ISSUED', 'APPENDIX 3', 'COE SENT'];

const BLANK = {
  student_id: '', last_name: '', first_name: '',
  program: '', province: '', agent: '', start_date: '', end_date: '', email: '',
  passport_pr_card: '', photo_id: '', transcript_12_acsw: '', language_proof: '',
  action: '', pending_documents: '', appendix_3: '',
  coe_status: 'not_done', sec_form: '', comments: '',
};

const YES_NO_OPTIONS = [
  { value: '',    label: '— Select —' },
  { value: 'yes', label: 'Yes' },
  { value: 'no',  label: 'No' },
];
// Legacy tolerance: old records may still have booleans in Mongo.
const yn = (v) => (v === true ? 'yes' : v === false ? '' : v || '');

const CoeBadge = ({ v }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${
    v === 'done' ? 'bg-[#E1F3E4] text-[#0B5E2A]' : 'bg-[#FEF4DB] text-[#8a5e00]'
  }`}>{v === 'done' ? 'Done' : 'Pending'}</span>
);
const SecBadge = ({ v }) => (
  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${
    v === 'yes' ? 'bg-[#E1F3E4] text-[#0B5E2A]' : v === 'no' ? 'bg-[#FDE2E2] text-[#9b1c1c]' : 'bg-[#F0F2EE] text-[#7a8a7c]'
  }`}>{v || '—'}</span>
);
const ProgramPill = ({ code }) => {
  if (!code) return <span className="text-[#9aa89c]">—</span>;
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[11.5px] font-bold text-[#0B5E2A] bg-[#E9F3EB] border border-[#0B5E2A]/15 tracking-wide">
      {code}
    </span>
  );
};
const ProvincePill = ({ p }) => (
  p ? <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11.5px] font-semibold text-[#4b5e4d] bg-[#f4f7f2] border border-[#0B5E2A]/10">{p}</span>
    : <span className="text-[#9aa89c]">—</span>
);
const DocPill = ({ v, label }) => {
  const val = v === true ? 'yes' : v === false ? '' : v || '';
  const tone = val === 'yes'
    ? 'bg-[#E1F3E4] text-[#0B5E2A] border-[#0B5E2A]/40'
    : val === 'no'
      ? 'bg-[#FDE2E2] text-[#9b1c1c] border-[#9b1c1c]/30'
      : 'bg-[#F0F2EE] text-[#7a8a7c] border-[#0B5E2A]/10';
  return (
    <span title={label} className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10.5px] font-bold uppercase border ${tone}`}>
      {val || '—'}
    </span>
  );
};
const ActionPill = ({ v }) => {
  if (!v) return <span className="text-[#9aa89c]">—</span>;
  const tone = {
    'Pending':       'bg-[#FEF4DB] text-[#8a5e00] border-[#F0B93C]/40',
    'ISSUE LOA':     'bg-[#FEF1E1] text-[#a05a1a] border-[#E9954B]/40',
    'ISSUE CLOA':    'bg-[#FEF1E1] text-[#a05a1a] border-[#E9954B]/40',
    'LOA ISSUED':    'bg-[#E1F0F3] text-[#0f5a6e] border-[#3AA6C0]/40',
    'CLOA ISSUED':   'bg-[#E1F0F3] text-[#0f5a6e] border-[#3AA6C0]/40',
    'APPENDIX 3':    'bg-[#EDE9F7] text-[#4b3a94] border-[#7A64C6]/40',
    'COE SENT':      'bg-[#E1F3E4] text-[#0B5E2A] border-[#0B5E2A]/40',
  }[v] || 'bg-[#F0F2EE] text-[#4b5e4d] border-[#0B5E2A]/15';
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wide border ${tone} whitespace-nowrap`}>{v}</span>
  );
};

// ---------- Reusable modal fields (outside RecordModal so inputs keep focus while typing) ----------
const ModalField = ({ label, name, form, upd, type = 'text', required, span = 1 }) => (
  <div className={span === 2 ? 'sm:col-span-2' : span === 3 ? 'sm:col-span-3' : ''}>
    <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#6b7e6d] mb-1">
      {label}{required && <span className="text-[#9b1c1c] ml-1">*</span>}
    </label>
    <input
      type={type} value={form[name] || ''} onChange={upd(name)}
      data-testid={`aid-field-${name}`}
      className="w-full px-3 py-2 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30"
    />
  </div>
);
const ModalSelect = ({ label, name, form, upd, options, required, span = 1 }) => (
  <div className={span === 2 ? 'sm:col-span-2' : ''}>
    <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#6b7e6d] mb-1">
      {label}{required && <span className="text-[#9b1c1c] ml-1">*</span>}
    </label>
    <select
      value={form[name] || ''} onChange={upd(name)}
      data-testid={`aid-field-${name}`}
      className="w-full px-3 py-2 rounded-md border border-[#0B5E2A]/20 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30"
    >
      {options.map((o) => (
        <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
      ))}
    </select>
  </div>
);
const ModalCheck = ({ label, name, form, upd }) => (
  <label className="flex items-center gap-2 text-sm text-[#2e4030] py-2">
    <input
      type="checkbox" checked={!!form[name]} onChange={upd(name)}
      data-testid={`aid-field-${name}`} className="accent-[#0B5E2A]"
    />
    {label}
  </label>
);
const ModalTextArea = ({ label, name, form, upd, rows = 2, span = 3 }) => (
  <div className={span === 3 ? 'sm:col-span-3' : span === 2 ? 'sm:col-span-2' : ''}>
    <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#6b7e6d] mb-1">{label}</label>
    <textarea
      rows={rows} value={form[name] || ''} onChange={upd(name)}
      data-testid={`aid-field-${name}`}
      className="w-full px-3 py-2 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30"
    />
  </div>
);

// ---------- Excel-style column filter (multi-select) ----------
// `valueLabel` maps a raw stored value (e.g. "yes", "not_done") to a nice label.
const FilterableHeader = ({ label, col, records, selected, onChange, valueLabel }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const rootRef = useRef(null);

  // Close on outside click / Esc
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const uniqueValues = useMemo(() => {
    const set = new Set();
    records.forEach((r) => set.add(String(r[col] ?? '').trim()));
    return Array.from(set).sort((a, b) => {
      if (a === '') return 1;
      if (b === '') return -1;
      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    });
  }, [records, col]);

  const displayLabel = (v) => (v === '' ? '(Blanks)' : (valueLabel ? valueLabel(v) : v));

  const visibleValues = useMemo(() => {
    if (!q.trim()) return uniqueValues;
    const query = q.toLowerCase();
    return uniqueValues.filter((v) => displayLabel(v).toLowerCase().includes(query));
  }, [uniqueValues, q, valueLabel]);

  const active = !!selected && selected.size > 0;
  const toggle = (v) => {
    const next = new Set(selected || []);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(next);
  };
  const selectAll = () => onChange(new Set(uniqueValues));
  const clear = () => onChange(new Set());

  const allSelectedInView = visibleValues.length > 0 && visibleValues.every((v) => (selected || new Set()).has(v));
  const toggleAllInView = () => {
    const next = new Set(selected || []);
    if (allSelectedInView) visibleValues.forEach((v) => next.delete(v));
    else visibleValues.forEach((v) => next.add(v));
    onChange(next);
  };

  return (
    <div className="relative inline-flex items-center gap-1.5" ref={rootRef}>
      <span>{label}</span>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`aid-filter-${col}`}
        title={active ? 'Filtered — click to change' : 'Filter'}
        className={`p-1 rounded transition-colors ${
          active ? 'text-[#F0B93C] bg-white/10' : 'text-white/80 hover:text-white hover:bg-white/10'
        }`}
      >
        <Filter className={`h-3.5 w-3.5 ${active ? 'fill-current' : ''}`} strokeWidth={active ? 2.5 : 1.8} />
      </button>
      {open && (
        <div
          className="absolute left-0 top-[calc(100%+6px)] z-30 w-64 bg-white text-[#1b2a1f] rounded-lg shadow-2xl border border-[#0B5E2A]/15 overflow-hidden normal-case tracking-normal"
          onClick={(e) => e.stopPropagation()}
          data-testid={`aid-filter-menu-${col}`}
        >
          <div className="p-2 border-b border-[#0B5E2A]/10 bg-[#f7faf5]">
            <div className="text-[10px] font-bold tracking-[0.14em] text-[#0B5E2A] uppercase mb-1">Filter · {label}</div>
            <input
              autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search values…" type="text"
              data-testid={`aid-filter-search-${col}`}
              className="w-full px-2 py-1 rounded-md border border-[#0B5E2A]/20 text-[12px] focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {visibleValues.length === 0 && (
              <div className="px-3 py-4 text-center text-[12px] text-[#7a8a7c]">No matches</div>
            )}
            {visibleValues.length > 0 && (
              <label className="flex items-center gap-2 px-3 py-1.5 text-[12px] text-[#4b5e4d] cursor-pointer hover:bg-[#f0f6ee] border-b border-[#0B5E2A]/5">
                <input type="checkbox" checked={allSelectedInView} onChange={toggleAllInView} className="accent-[#0B5E2A]" />
                <span className="font-semibold">Select all {q ? '(matches)' : ''}</span>
              </label>
            )}
            {visibleValues.map((v) => (
              <label key={v || '__blank'} className="flex items-center gap-2 px-3 py-1.5 text-[12.5px] cursor-pointer hover:bg-[#f0f6ee]">
                <input
                  type="checkbox"
                  checked={(selected || new Set()).has(v)}
                  onChange={() => toggle(v)}
                  className="accent-[#0B5E2A]"
                />
                <span className={v === '' ? 'italic text-[#7a8a7c]' : ''}>{displayLabel(v)}</span>
              </label>
            ))}
          </div>
          <div className="flex items-center justify-between px-2 py-2 border-t border-[#0B5E2A]/10 bg-[#fafbf7]">
            <button onClick={clear} type="button" data-testid={`aid-filter-clear-${col}`}
              className="text-[12px] text-[#9b1c1c] font-semibold hover:underline">Clear</button>
            <div className="flex gap-2">
              <button onClick={selectAll} type="button"
                className="text-[12px] text-[#4b5e4d] hover:underline">Select all</button>
              <button onClick={() => setOpen(false)} type="button"
                className="text-[12px] font-bold text-[#0B5E2A] hover:underline">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Modal (Add / Edit) ----------
const RecordModal = ({ initial, onClose, onSaved, recruiters }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    ...BLANK,
    ...(initial || {}),
    // Coerce legacy bool doc fields to string ("yes"/"no"/"")
    passport_pr_card:   yn(initial?.passport_pr_card),
    photo_id:           yn(initial?.photo_id),
    transcript_12_acsw: yn(initial?.transcript_12_acsw),
    language_proof:     yn(initial?.language_proof),
  });
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.id;

  const upd = (name) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [name]: val }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.student_id.trim() || !form.first_name.trim() || !form.last_name.trim()) {
      toast({ title: 'Missing required fields', description: 'Student ID, First Name, Last Name.' });
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, student_id: form.student_id.trim() };
      let saved;
      if (isEdit) {
        const { data } = await api.patch(`/admin/student-aid/${initial.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post('/admin/student-aid', payload);
        saved = data;
      }
      toast({ title: isEdit ? 'Record updated' : 'Record created' });
      onSaved(saved);
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 pointer-events-none">
        <form
          onSubmit={submit}
          className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[calc(100vh-3rem)] pointer-events-auto"
          data-testid="aid-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-7 py-5 border-b border-[#0B5E2A]/10 rounded-t-2xl flex-shrink-0 bg-white">
            <div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-[#6b7e6d] font-semibold">Student Aid Master Record</div>
              <h3 className="font-display text-2xl text-[#0B5E2A] mt-0.5">
                {isEdit ? 'Edit Record' : 'Add New Record'}
              </h3>
            </div>
            <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-[#f0f6ee] text-[#4b5e4d]" data-testid="aid-modal-close">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-7 py-6 space-y-8" data-testid="aid-modal-body">
            {/* ---- Basic Info ---- */}
            <div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-[#0B5E2A] font-bold mb-3 flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#F0B93C]" /> Basic Info
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <ModalField label="Student ID"  name="student_id" form={form} upd={upd} required />
                <ModalField label="Last Name"   name="last_name"  form={form} upd={upd} required />
                <ModalField label="First Name"  name="first_name" form={form} upd={upd} required />
                <ModalField label="Email" name="email" form={form} upd={upd} type="email" span={3} />
              </div>
            </div>

            {/* ---- Program & Duration ---- */}
            <div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-[#0B5E2A] font-bold mb-3 flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#F0B93C]" /> Program & Duration
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <ModalSelect label="Program" name="program" form={form} upd={upd} options={[
                  { value: '', label: '— Select —' },
                  ...AID_PROGRAMS.map((p) => ({ value: p.code, label: `${p.name} (${p.code})` })),
                ]} />
                <ModalSelect label="Province" name="province" form={form} upd={upd} options={[
                  { value: '', label: '— Select —' }, ...PROVINCES,
                ]} />
                <ModalSelect label="Agent / Agency" name="agent" form={form} upd={upd} options={(() => {
                  const opts = [{ value: '', label: '— Select —' }];
                  const names = new Set();
                  (recruiters || []).forEach((r) => {
                    if (!r?.name) return;
                    if (names.has(r.name)) return;
                    names.add(r.name);
                    const suffix = r.company ? ` — ${r.company}` : '';
                    const tag = r.status === 'internal' ? '  •  Internal'
                      : r.status === 'active' ? ''
                      : r.status === 'agreement_signed' ? '  •  Signed'
                      : r.status === 'pending' ? '  •  Pending'
                      : `  •  ${r.status || ''}`.trimEnd();
                    opts.push({ value: r.name, label: `${r.name}${suffix}${tag}` });
                  });
                  // Preserve legacy free-text agent value that no longer matches any recruiter
                  const cur = (form.agent || '').trim();
                  if (cur && !names.has(cur)) {
                    opts.push({ value: cur, label: `${cur}  •  (legacy)` });
                  }
                  return opts;
                })()} />
                <ModalField label="Start Date" name="start_date" form={form} upd={upd} type="date" />
                <ModalField label="End Date"   name="end_date"   form={form} upd={upd} type="date" />
              </div>
            </div>

            {/* ---- Document Checklist ---- */}
            <div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-[#0B5E2A] font-bold mb-3 flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#F0B93C]" /> Document Checklist
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-xl border border-[#0B5E2A]/15 bg-[#fafcf7] px-5 py-4">
                <ModalSelect label="Passport / PR Card"     name="passport_pr_card"   form={form} upd={upd} options={YES_NO_OPTIONS} />
                <ModalSelect label="Photo ID Proof"         name="photo_id"           form={form} upd={upd} options={YES_NO_OPTIONS} />
                <ModalSelect label="Transcript / 12 (ACSW)" name="transcript_12_acsw" form={form} upd={upd} options={YES_NO_OPTIONS} />
                <ModalSelect label="Language Proof"         name="language_proof"     form={form} upd={upd} options={YES_NO_OPTIONS} />
              </div>
            </div>

            {/* ---- Status & Actions ---- */}
            <div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-[#0B5E2A] font-bold mb-3 flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#F0B93C]" /> Status & Actions
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <ModalSelect label="Action" name="action" form={form} upd={upd} options={ACTIONS.map((a) => ({ value: a, label: a || '— Select —' }))} />
                <ModalSelect label="COE Status" name="coe_status" form={form} upd={upd} options={[
                  { value: 'not_done', label: 'Not done' },
                  { value: 'done',     label: 'Done' },
                ]} />
                <ModalSelect label="SEC Form" name="sec_form" form={form} upd={upd} options={[
                  { value: '',    label: '— Select —' },
                  { value: 'yes', label: 'Yes' },
                  { value: 'no',  label: 'No' },
                ]} />
                <ModalField label="Appendix 3" name="appendix_3" form={form} upd={upd} span={3} />
              </div>
            </div>

            {/* ---- Notes ---- */}
            <div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-[#0B5E2A] font-bold mb-3 flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#F0B93C]" /> Notes
              </div>
              <div className="grid grid-cols-1 gap-5">
                <ModalTextArea label="Pending Documents" name="pending_documents" form={form} upd={upd} rows={2} span={3} />
                <ModalTextArea label="Comments"          name="comments"          form={form} upd={upd} rows={3} span={3} />
              </div>
            </div>
          </div>

          {/* Fixed footer */}
          <div className="flex justify-end gap-3 px-7 py-4 border-t border-[#0B5E2A]/10 rounded-b-2xl flex-shrink-0 bg-white">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-md border border-[#0B5E2A]/25 text-[#0B5E2A] font-semibold hover:bg-[#f0f6ee]" data-testid="aid-modal-cancel">
              Cancel
            </button>
            <button type="submit" disabled={busy} data-testid="aid-modal-save"
              className="btn-green inline-flex items-center gap-2 px-6 py-2.5 rounded-md disabled:opacity-60">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving...</> : <><Save className="h-4 w-4" /> Save Record</>}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

// ---------- Main Tab ----------
const StudentAidTab = () => {
  const { toast } = useToast();
  const [records, setRecords] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  // Excel-style column filters. Each entry is a Set of selected raw values (empty Set = no filter).
  const [colFilters, setColFilters] = useState({
    program: new Set(), province: new Set(), agent: new Set(),
    action: new Set(), coe_status: new Set(), sec_form: new Set(),
  });
  const setFilterFor = (col) => (set) => setColFilters((f) => ({ ...f, [col]: set }));
  const clearAllFilters = () => setColFilters({
    program: new Set(), province: new Set(), agent: new Set(),
    action: new Set(), coe_status: new Set(), sec_form: new Set(),
  });
  const anyFilterActive = Object.values(colFilters).some((s) => s.size > 0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aid, recs] = await Promise.all([
        api.get('/admin/student-aid'),
        api.get('/admin/recruiters').catch(() => ({ data: [] })),
      ]);
      setRecords(aid.data || []);
      // Sort recruiters alphabetically by name for a predictable dropdown
      const sorted = (recs.data || []).slice().sort((a, b) =>
        String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' })
      );
      setRecruiters(sorted);
    } catch (e) {
      toast({ title: 'Failed to load', description: e?.response?.data?.detail || 'Try again' });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return records.filter((r) => {
      // Column filters (AND across columns; OR within a column's selected set)
      for (const [col, set] of Object.entries(colFilters)) {
        if (!set || set.size === 0) continue;
        const val = String(r[col] ?? '').trim();
        if (!set.has(val)) return false;
      }
      if (!query) return true;
      return [
        r.student_id, r.first_name, r.last_name, r.email, r.program, r.province, r.agent,
        r.action, r.comments, r.pending_documents, r.appendix_3,
      ].some((v) => String(v || '').toLowerCase().includes(query));
    });
  }, [records, q, colFilters]);

  const onSaved = (rec) => {
    setRecords((xs) => {
      const i = xs.findIndex((r) => r.id === rec.id);
      if (i === -1) return [rec, ...xs];
      const c = [...xs]; c[i] = rec; return c;
    });
    setShowModal(false);
    setEditing(null);
  };

  const onDelete = async (rec) => {
    if (!window.confirm(`Delete record for ${rec.first_name} ${rec.last_name} (${rec.student_id})?`)) return;
    try {
      await api.delete(`/admin/student-aid/${rec.id}`);
      setRecords((xs) => xs.filter((x) => x.id !== rec.id));
      toast({ title: 'Deleted' });
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again' });
    }
  };

  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const onImportPick = () => fileInputRef.current?.click();

  const onImportChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-uploading same file
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      toast({ title: 'Wrong file type', description: 'Please upload an .xlsx file.' });
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_BASE}/admin/student-aid/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body?.detail || `HTTP ${r.status}`);
      const parts = [];
      if (body.created) parts.push(`${body.created} created`);
      if (body.updated) parts.push(`${body.updated} updated`);
      if (body.skipped) parts.push(`${body.skipped} skipped`);
      toast({
        title: 'Import complete',
        description: (parts.join(' · ') || 'No rows changed')
          + (body.errors?.length ? `  ·  ${body.errors.length} error${body.errors.length === 1 ? '' : 's'}` : ''),
      });
      if (body.errors?.length) {
        console.warn('Import errors:', body.errors);
      }
      await load();
    } catch (err) {
      toast({ title: 'Import failed', description: err.message });
    } finally {
      setImporting(false);
    }
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const r = await fetch(`${API_BASE}/admin/student-aid/export`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replaceAll('-', '');
      a.download = `HCC-Student-Aid-${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({ title: 'Excel downloaded' });
    } catch (e) {
      toast({ title: 'Export failed', description: e.message });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="student-aid-tab">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-[#6b7e6d]">
          <ClipboardList className="h-4 w-4" /> {filtered.length} record{filtered.length === 1 ? '' : 's'}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-[#0B5E2A]/50" />
            <input
              placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="aid-search"
              className="pl-8 pr-3 py-1.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30"
            />
          </div>
          <button onClick={load} disabled={loading} data-testid="aid-refresh"
            className="inline-flex items-center gap-1.5 text-sm text-[#0B5E2A] hover:bg-[#f0f6ee] px-3 py-1.5 rounded">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            onChange={onImportChange}
            data-testid="aid-import-file"
            className="hidden"
          />
          <button onClick={onImportPick} disabled={importing} data-testid="aid-import-btn"
            title="Upload an .xlsx file to bulk-add / update records"
            className="inline-flex items-center gap-1.5 text-sm text-[#0B5E2A] border border-[#0B5E2A]/25 hover:bg-[#f0f6ee] px-3 py-1.5 rounded disabled:opacity-50">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Upload Excel
          </button>
          <button onClick={onExport} disabled={exporting || filtered.length === 0} data-testid="aid-export-btn"
            title={filtered.length === 0 ? 'No records to export' : 'Download all records as .xlsx'}
            className="inline-flex items-center gap-1.5 text-sm text-[#0B5E2A] border border-[#0B5E2A]/25 hover:bg-[#f0f6ee] px-3 py-1.5 rounded disabled:opacity-50">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            Download Excel
          </button>
          <button onClick={() => { setEditing(null); setShowModal(true); }} data-testid="aid-add-btn"
            className="btn-green inline-flex items-center gap-2 px-4 py-1.5 rounded-md">
            <Plus className="h-4 w-4" /> Add Record
          </button>
        </div>
      </div>

      {/* Active filter chips */}
      {anyFilterActive && (
        <div className="flex items-center gap-2 flex-wrap mb-3 px-3 py-2 rounded-lg bg-[#FEF8E6] border border-[#F0B93C]/40" data-testid="aid-filter-chips">
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#8a5e00]">Filters:</span>
          {Object.entries(colFilters).map(([col, set]) => (set && set.size > 0) && (
            <span key={col} className="inline-flex items-center gap-1.5 bg-white border border-[#F0B93C]/60 text-[#8a5e00] text-[11.5px] font-semibold px-2 py-1 rounded-md">
              <span className="uppercase tracking-wider text-[10px] text-[#a97400]">{col.replace('_', ' ')}</span>
              <span>·</span>
              <span>{Array.from(set).map((v) => v === '' ? '(Blanks)' : v).join(', ')}</span>
              <button onClick={() => setFilterFor(col)(new Set())} className="ml-1 text-[#9b1c1c] hover:bg-[#FDE2E2] rounded p-0.5"
                data-testid={`aid-filter-chip-remove-${col}`}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button onClick={clearAllFilters} data-testid="aid-filters-clear-all"
            className="ml-auto text-[11.5px] font-semibold text-[#0B5E2A] hover:underline">Clear all filters</button>
        </div>
      )}

      <div className="bg-white border border-[#0B5E2A]/10 rounded-2xl shadow-[0_1px_0_rgba(11,94,42,0.04),0_10px_30px_-15px_rgba(11,94,42,0.15)] overflow-hidden">
        <div className="overflow-x-auto overflow-y-visible">
          <table className="w-full text-[13px] border-separate border-spacing-0">
            <colgroup>
              <col style={{ width: 130 }} />
              <col style={{ width: 220 }} />
              <col style={{ width: 120 }} />
              <col style={{ width: 100 }} />
              <col style={{ width: 160 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 240 }} />
              <col style={{ width: 84 }} />
              <col style={{ width: 84 }} />
              <col style={{ width: 84 }} />
              <col style={{ width: 84 }} />
              <col style={{ width: 170 }} />
              <col style={{ width: 220 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 100 }} />
              <col style={{ width: 260 }} />
              <col style={{ width: 100 }} />
            </colgroup>
            <thead className="bg-gradient-to-b from-[#0B5E2A] to-[#0a4d24] text-white text-[10.5px] uppercase tracking-[0.14em] font-bold">
              <tr>
                <th className="text-left px-4 py-3.5 sticky left-0 bg-[#0B5E2A] z-10 border-r border-white/10">Student ID</th>
                <th className="text-left px-4 py-3.5">Name</th>
                <th className="text-left px-4 py-3.5">
                  <FilterableHeader label="Program" col="program" records={records}
                    selected={colFilters.program} onChange={setFilterFor('program')} />
                </th>
                <th className="text-left px-4 py-3.5">
                  <FilterableHeader label="Province" col="province" records={records}
                    selected={colFilters.province} onChange={setFilterFor('province')} />
                </th>
                <th className="text-left px-4 py-3.5">
                  <FilterableHeader label="Agent" col="agent" records={records}
                    selected={colFilters.agent} onChange={setFilterFor('agent')} />
                </th>
                <th className="text-left px-4 py-3.5">Start Date</th>
                <th className="text-left px-4 py-3.5">End Date</th>
                <th className="text-left px-4 py-3.5">Email</th>
                <th className="text-center px-2 py-3.5" title="Passport / PR Card">Ppt/PR</th>
                <th className="text-center px-2 py-3.5" title="Photo ID Proof">Photo ID</th>
                <th className="text-center px-2 py-3.5" title="Transcript / Grade 12 for ACSW">Tr/12</th>
                <th className="text-center px-2 py-3.5" title="Language Proof">Lang</th>
                <th className="text-left px-4 py-3.5">
                  <FilterableHeader label="Action" col="action" records={records}
                    selected={colFilters.action} onChange={setFilterFor('action')} />
                </th>
                <th className="text-left px-4 py-3.5">Pending Docs</th>
                <th className="text-left px-4 py-3.5">Appendix 3</th>
                <th className="text-center px-3 py-3.5">
                  <FilterableHeader label="COE" col="coe_status" records={records}
                    selected={colFilters.coe_status} onChange={setFilterFor('coe_status')}
                    valueLabel={(v) => v === 'done' ? 'Done' : (v === 'not_done' ? 'Not done' : v || '(Blanks)')} />
                </th>
                <th className="text-center px-2 py-3.5">
                  <FilterableHeader label="SEC" col="sec_form" records={records}
                    selected={colFilters.sec_form} onChange={setFilterFor('sec_form')}
                    valueLabel={(v) => v === 'yes' ? 'Yes' : (v === 'no' ? 'No' : v || '(Blanks)')} />
                </th>
                <th className="text-left px-4 py-3.5">Comments</th>
                <th className="text-right px-4 py-3.5 pr-5 sticky right-0 bg-[#0B5E2A] z-10 border-l border-white/10">Actions</th>
              </tr>
            </thead>
            <tbody className="text-[#1b2a1f]">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={18} className="text-center py-16 text-[#6b7e6d] bg-white">
                    <ClipboardList className="h-9 w-9 mx-auto mb-3 opacity-40" />
                    <div className="font-semibold">No student aid records yet</div>
                    <div className="text-xs mt-1">Click <span className="font-bold text-[#0B5E2A]">Add Record</span> to create your first entry.</div>
                  </td>
                </tr>
              )}
              {filtered.map((r, idx) => (
                <tr
                  key={r.id}
                  data-testid={`aid-row-${r.id}`}
                  className={`group ${idx % 2 === 0 ? 'bg-white' : 'bg-[#fbfdf9]'} hover:bg-[#f4f9f0] transition-colors`}
                >
                  <td className={`px-4 py-3.5 font-bold text-[#0B5E2A] sticky left-0 z-[1] border-t border-[#0B5E2A]/8 ${idx % 2 === 0 ? 'bg-white' : 'bg-[#fbfdf9]'} group-hover:bg-[#f4f9f0]`}>
                    {r.student_id}
                  </td>
                  <td className="px-4 py-3.5 border-t border-[#0B5E2A]/8">
                    <div className="font-semibold">{`${r.last_name || ''}${r.last_name && r.first_name ? ', ' : ''}${r.first_name || ''}`}</div>
                    {r.email && <div className="text-[11px] text-[#7a8a7c] mt-0.5">{r.email}</div>}
                  </td>
                  <td className="px-4 py-3.5 border-t border-[#0B5E2A]/8"><ProgramPill code={r.program} /></td>
                  <td className="px-4 py-3.5 border-t border-[#0B5E2A]/8"><ProvincePill p={r.province} /></td>
                  <td className="px-4 py-3.5 border-t border-[#0B5E2A]/8 whitespace-nowrap text-[12.5px] text-[#4b5e4d]">{r.start_date || '—'}</td>
                  <td className="px-4 py-3.5 border-t border-[#0B5E2A]/8 whitespace-nowrap text-[12.5px] text-[#4b5e4d]">{r.end_date || '—'}</td>
                  <td className="px-4 py-3.5 border-t border-[#0B5E2A]/8 text-[12.5px] truncate max-w-[240px]">{r.email || '—'}</td>
                  <td className="px-2 py-3.5 border-t border-[#0B5E2A]/8 text-center"><DocPill v={r.passport_pr_card} label="Passport / PR" /></td>
                  <td className="px-2 py-3.5 border-t border-[#0B5E2A]/8 text-center"><DocPill v={r.photo_id} label="Photo ID" /></td>
                  <td className="px-2 py-3.5 border-t border-[#0B5E2A]/8 text-center"><DocPill v={r.transcript_12_acsw} label="Transcript / 12 (ACSW)" /></td>
                  <td className="px-2 py-3.5 border-t border-[#0B5E2A]/8 text-center"><DocPill v={r.language_proof} label="Language Proof" /></td>
                  <td className="px-4 py-3.5 border-t border-[#0B5E2A]/8"><ActionPill v={r.action} /></td>
                  <td className="px-4 py-3.5 border-t border-[#0B5E2A]/8 text-[12.5px] truncate max-w-[220px]" title={r.pending_documents}>{r.pending_documents || <span className="text-[#c9c9c9]">—</span>}</td>
                  <td className="px-4 py-3.5 border-t border-[#0B5E2A]/8 text-[12.5px] truncate max-w-[150px]" title={r.appendix_3}>{r.appendix_3 || <span className="text-[#c9c9c9]">—</span>}</td>
                  <td className="px-3 py-3.5 border-t border-[#0B5E2A]/8 text-center"><CoeBadge v={r.coe_status} /></td>
                  <td className="px-2 py-3.5 border-t border-[#0B5E2A]/8 text-center"><SecBadge v={r.sec_form} /></td>
                  <td className="px-4 py-3.5 border-t border-[#0B5E2A]/8 text-[12.5px] truncate max-w-[260px]" title={r.comments}>{r.comments || <span className="text-[#c9c9c9]">—</span>}</td>
                  <td className={`px-4 py-3.5 border-t border-[#0B5E2A]/8 text-right pr-4 sticky right-0 z-[1] ${idx % 2 === 0 ? 'bg-white' : 'bg-[#fbfdf9]'} group-hover:bg-[#f4f9f0]`}>
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => { setEditing(r); setShowModal(true); }} title="Edit"
                        data-testid={`aid-edit-${r.id}`}
                        className="h-8 w-8 rounded-md text-[#0B5E2A] hover:bg-[#E1F3E4] inline-flex items-center justify-center transition-colors">
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button onClick={() => onDelete(r)} title="Delete"
                        data-testid={`aid-delete-${r.id}`}
                        className="h-8 w-8 rounded-md text-[#9b1c1c] hover:bg-[#FDE2E2] inline-flex items-center justify-center transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <RecordModal
          initial={editing}
          recruiters={recruiters}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSaved={onSaved}
        />
      )}
    </div>
  );
};

export default StudentAidTab;
